import { Box, createStyles, Menu, rem, Tooltip } from '@mantine/core';
import P2Regular from 'components/typography/P2Regular';
import P4Medium from 'components/typography/P4Medium';
import { useMemo } from 'react';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';

export type SimpleControlKind = 'action-icon' | 'button' | 'menu-item';

export interface SimpleControlProps {
  kind: SimpleControlKind;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  shortcut?: string;
  onClick?: () => void;
  minWidth?: number;
}

const useStyles = createStyles((theme, { withIcon, minWidth }: { withIcon: boolean; minWidth?: number }) => ({
  icon: {
    '& svg': {
      strokeWidth: '2.5 !important',
    },
  },
  itemWrapper: {
    minWidth: minWidth ? rem(minWidth) : rem(300),
  },
  itemInner: {
    height: rem(36),
    display: 'grid',
    gridTemplateColumns: withIcon ? 'auto 1fr auto' : '1fr',
    alignItems: 'center',
    justifyItems: withIcon ? 'start' : 'center',
    gap: rem(8),
    padding: `${rem(8)} ${rem(16)}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
    '& svg': {
      width: rem(16),
      height: rem(16),
    },
  },
}));

/**
 * A toolbar control action button for operations without a dropdown.
 */
export default function SimpleControl({
  kind,
  icon,
  label,
  active,
  disabled,
  shortcut,
  onClick,
  minWidth,
}: SimpleControlProps) {
  const { classes } = useStyles({ withIcon: !!icon, minWidth });

  const tooltipLabel = useMemo(() => {
    if (shortcut) {
      return `${label} (${shortcut})`;
    }

    return label;
  }, [label, shortcut]);

  const iconVariant = useMemo(() => (active ? 'subtle-blue' : 'subtle-gray'), [active]);

  if (kind === 'action-icon') {
    return (
      <Tooltip label={tooltipLabel} withArrow={false} openDelay={500} withinPortal position="bottom">
        <ActionIcon variant={iconVariant} size="xs" onClick={onClick} className={classes.icon} disabled={disabled}>
          {icon}
        </ActionIcon>
      </Tooltip>
    );
  }

  if (kind === 'button') {
    return (
      <Button variant="subtle-gray" size="xs" onClick={onClick} leftIcon={icon} disabled={disabled}>
        {label}
      </Button>
    );
  }

  return (
    <Box className={classes.itemWrapper}>
      <Menu.Item pos="relative" onClick={onClick} p={0} disabled={disabled}>
        <Box className={classes.itemInner} opacity={disabled ? 0.7 : 1}>
          {icon}
          <P2Regular c={disabled ? 'gray.6' : 'gray.8'}>{label}</P2Regular>
          <P4Medium c={disabled ? 'gray.5' : 'gray.6'}>{shortcut}</P4Medium>
        </Box>
      </Menu.Item>
    </Box>
  );
}
